import React, { useCallback, useTransition } from "react";
import { useLocation } from "react-router-dom";
import { useNavigateWithSearch } from "hooks/useNavigateWithSearch";
import { APPLICATION_CARD_ORDER } from "constants/pageNames";
import { ROUTES } from "routes/constants";
import type {
    ContactInformationRef,
    CoverageReplacementRef,
    PolicyBeneficiaryRef,
    SecondaryAddresseeRef,
    StatementOfHealthRef,
} from "types/Card";

const [contactInfoCardId, , coverageReplacementCardId, policyBeneficiaryCardId, secondaryAddresseeCardId] =
    APPLICATION_CARD_ORDER;

interface SubmitRefs {
    contactInformation: React.RefObject<ContactInformationRef | null>;
    statementOfHealth: React.RefObject<StatementOfHealthRef | null> | null;
    coverageReplacement: React.RefObject<CoverageReplacementRef | null> | null;
    policyBeneficiary: React.RefObject<PolicyBeneficiaryRef | null>;
    secondaryAddressee: React.RefObject<SecondaryAddresseeRef | null>;
}

interface UseContactSubmissionArgs {
    cardRefs: Record<string, React.RefObject<any>>;
    contactInformationRef: React.RefObject<ContactInformationRef | null>;
    statementOfHealthRef: React.RefObject<StatementOfHealthRef | null>;
    coverageReplacementRef: React.RefObject<CoverageReplacementRef | null>;
    policyBeneficiaryRef: React.RefObject<PolicyBeneficiaryRef | null>;
    secondaryAddresseeRef: React.RefObject<SecondaryAddresseeRef | null>;
    isDTO: boolean;
    isGIWL: boolean;
    scrollToCard: (cardId: string) => void;
    submitContactInformation: (refs: SubmitRefs) => Promise<void>;
    paymentToken: string | null | undefined;
}

/**
 * Drives the page's "Next" action: validates every rendered card, scrolls to
 * the first invalid one, otherwise submits the collected refs and routes onward
 * (skipping payment when a token already exists). Also exposes the Back handler.
 *
 * @returns `formAction` for the Next button, its `isPending` flag, and `handleBack`.
 */
export const useContactSubmission = ({
    cardRefs,
    contactInformationRef,
    statementOfHealthRef,
    coverageReplacementRef,
    policyBeneficiaryRef,
    secondaryAddresseeRef,
    isDTO,
    isGIWL,
    scrollToCard,
    submitContactInformation,
    paymentToken,
}: UseContactSubmissionArgs) => {
    const navigate = useNavigateWithSearch();
    const location = useLocation();
    const [isSubmitting, startSubmitTransition] = useTransition();

    const validateAllCards = useCallback(async () => {
        const nonDtoCards = isGIWL
            ? [contactInfoCardId, policyBeneficiaryCardId, secondaryAddresseeCardId, coverageReplacementCardId]
            : [contactInfoCardId, policyBeneficiaryCardId, secondaryAddresseeCardId];
        const cardsToValidate = isDTO ? APPLICATION_CARD_ORDER : nonDtoCards;

        const validationResults = await Promise.all(
            cardsToValidate.map(async (cardId) => ({
                cardId,
                isValid: await cardRefs[cardId].current!.validate(),
            }))
        );

        const firstInvalidCard = validationResults.find((result) => !result.isValid);

        if (firstInvalidCard) {
            navigate(`${location.pathname}#${firstInvalidCard.cardId}`, { replace: true });
            scrollToCard(firstInvalidCard.cardId);
            return false;
        }

        return true;
    }, [isDTO, isGIWL, cardRefs, navigate, location, scrollToCard]);

    const handleBack = useCallback(() => navigate(ROUTES.QUOTE), [navigate]);

    const formAction = useCallback(() => {
        startSubmitTransition(async () => {
            const isValid = await validateAllCards();
            if (!isValid) return;

            await submitContactInformation({
                contactInformation: contactInformationRef,
                statementOfHealth: isDTO ? statementOfHealthRef : null,
                coverageReplacement: isDTO || isGIWL ? coverageReplacementRef : null,
                policyBeneficiary: policyBeneficiaryRef,
                secondaryAddressee: secondaryAddresseeRef,
            });

            // Skip payment page if payment token already exists
            navigate(paymentToken ? ROUTES.APPLICATION.REVIEW : ROUTES.APPLICATION.PAYMENT);
        });
    }, [
        startSubmitTransition,
        validateAllCards,
        submitContactInformation,
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        isDTO,
        isGIWL,
        paymentToken,
        navigate,
    ]);

    return { formAction, isPending: isSubmitting, handleBack };
};
